// Tiny zero-dep IndexedDB wrapper for CueBoard
const DB_NAME = "cueboard";
const DB_VERSION = 1;

export type TxMode = "readonly" | "readwrite";

export function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains("packets")) {
        db.createObjectStore("packets", { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains("meta")) {
        const meta = db.createObjectStore("meta", { keyPath: "key" });
        // seed
        meta.put({ key: "schemaVersion", value: "v1" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function tx<T>(
  store: string,
  mode: TxMode,
  fn: (s: IDBObjectStore) => Promise<T> | T
): Promise<T> {
  const db = await openDB();
  return new Promise<T>((resolve, reject) => {
    const t = db.transaction(store, mode);
    const s = t.objectStore(store);
    Promise.resolve(fn(s)).then(
      (val) => {
        t.oncomplete = () => resolve(val);
        t.onerror = () => reject(t.error);
      },
      (err) => reject(err)
    );
  });
}

export async function getAll<T = any>(store: string): Promise<T[]> {
  return tx(store, "readonly", (s) => {
    return new Promise<T[]>((resolve, reject) => {
      const req = s.getAll();
      req.onsuccess = () => resolve(req.result as T[]);
      req.onerror = () => reject(req.error);
    });
  });
}

export async function getOne<T = any>(store: string, key: IDBValidKey): Promise<T | undefined> {
  return tx(store, "readonly", (s) => {
    return new Promise<T | undefined>((resolve, reject) => {
      const req = s.get(key);
      req.onsuccess = () => resolve(req.result as T | undefined);
      req.onerror = () => reject(req.error);
    });
  });
}

export async function putOne(store: string, value: any): Promise<void> {
  return tx(store, "readwrite", (s) => {
    return new Promise<void>((resolve, reject) => {
      const req = s.put(value);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  });
}

export async function deleteOne(store: string, key: IDBValidKey): Promise<void> {
  return tx(store, "readwrite", (s) => {
    return new Promise<void>((resolve, reject) => {
      const req = s.delete(key);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  });
}
